import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import supportTicketService from '../../services/supportTicketService';
import { useAuth } from '../../contexts/AuthContext';
import { TYPE_LABELS, PRIORITY_LABELS, STATUS_CLASSES, OPEN_STATUSES, statusText, formatDate, ticketContext, ticketProduct } from './supportLabels';
import '../SupportPage.css';

// THE support ticket list — every screen that shows tickets uses this (CLAUDE.md reusable-components rule): /support,
// the hotel and chain Support tabs, the partner Support tab, the dashboard's open tickets.
//
//   scope     'auto' (the server's default for the caller) | 'mine' (what I raised) | 'hotel' (+hotelId) | 'account' (+accountId: the chain and all its hotels)
//             | 'partner' (+partnerId) | 'all' (superadmin) | 'product' (+product, and productId — one instance — or
//             hotelId / accountId: a product's Support tab). The server checks each against the caller's access.
//   openOnly  only Open / In Progress, and no status filter (the dashboard)
//   title     optional heading; `emptyText` what to say when there are none; `limit` cap the rows shown
//   reloadKey change it to reload (e.g. after a ticket is raised)
//
// A row opens the ticket in hub (/support/:id). Which work queue it sits in is never shown.

function SupportTicketsPanel({ scope = 'mine', hotelId, accountId, partnerId, product, productId, openOnly = false, title, emptyText, limit, reloadKey }) {
    const navigate = useNavigate();
    const { isSuperAdmin } = useAuth();
    const [tickets, setTickets] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [status, setStatus] = useState('');
    const [search, setSearch] = useState('');

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            // 'auto': the server's default for the caller — everything (superadmin), the partner queue (a partner user), or
            // the tickets the user raised (anyone else).
            const params = { ...(scope !== 'auto' ? { scope } : {}), ...(hotelId ? { hotelId } : {}), ...(accountId ? { accountId } : {}), ...(partnerId ? { partnerId } : {}),
                ...(product ? { product } : {}), ...(productId ? { productId } : {}),
                ...(!openOnly && status ? { status } : {}) };
            const res = await supportTicketService.listTickets(params);
            setTickets(res.data?.tickets || []);
        } catch (e) {
            setError(e?.response?.status === 404 ? 'Not available' : 'Failed to load support tickets');
            setTickets([]);
        } finally {
            setLoading(false);
        }
    }, [scope, hotelId, accountId, partnerId, product, productId, openOnly, status]);

    useEffect(() => { load(); }, [load, reloadKey]);

    const shown = useMemo(() => {
        const q = search.trim().toLowerCase();
        let rows = openOnly ? tickets.filter((t) => OPEN_STATUSES.has(t.status)) : tickets;
        if (q) rows = rows.filter((t) => `${t.id} ${t.subject} ${t.description} ${ticketContext(t)} ${ticketProduct(t) || ''} ${t.created_by_email || ''}`.toLowerCase().includes(q));
        return limit ? rows.slice(0, limit) : rows;
    }, [tickets, openOnly, search, limit]);

    return (
        <div className="support-panel">
            {title && <h3 className="support-panel-title">{title}</h3>}
            {!openOnly && (
                <div className="support-filters">
                    <label>Status:</label>
                    <select value={status} onChange={(e) => setStatus(e.target.value)}>
                        <option value="">All</option>
                        <option value="open">Open</option>
                        <option value="in_progress">In Progress</option>
                        <option value="resolved">Resolved</option>
                        <option value="closed">Closed</option>
                    </select>
                    <input type="search" placeholder="Search tickets…" value={search} onChange={(e) => setSearch(e.target.value)} />
                </div>
            )}
            {loading ? (
                <div className="support-loading">Loading tickets...</div>
            ) : error ? (
                <div className="support-error">{error}</div>
            ) : !shown.length ? (
                <div className="support-empty"><p>{emptyText || (openOnly ? 'No open tickets' : 'No tickets found')}</p></div>
            ) : (
                <div className="support-table-wrap">
                    <table className="support-table">
                        <thead>
                            <tr>
                                <th>#</th><th>Subject</th><th>Relates to</th>
                                {!openOnly && <th>Type</th>}<th>Priority</th><th>Status</th>
                                <th>Raised by</th><th>Created</th>
                            </tr>
                        </thead>
                        <tbody>
                            {shown.map((t) => (
                                <tr key={t.id} className="support-row-clickable" onClick={() => navigate(`/support/${t.id}`)}>
                                    <td className="ticket-id">#{t.id}</td>
                                    <td className="ticket-subject">
                                        <div className="ticket-subject-text">{t.subject}</div>
                                        <div className="ticket-description-preview">{t.description}</div>
                                    </td>
                                    <td className="ticket-context">
                                        {ticketContext(t)}
                                        {ticketProduct(t) && <div className="ticket-description-preview">{ticketProduct(t)}</div>}
                                    </td>
                                    {!openOnly && <td><span className="badge badge-type">{TYPE_LABELS[t.type] || t.type}</span></td>}
                                    <td><span className={`badge badge-priority-${t.priority}`}>{PRIORITY_LABELS[t.priority] || t.priority}</span></td>
                                    <td>
                                        <span className={`badge ${STATUS_CLASSES[t.status] || ''}`}>{statusText(t)}</span>
                                        {isSuperAdmin && t.jira_key && <span className="support-sa-key sa-only"> {t.jira_key}</span>}
                                    </td>
                                    <td className="ticket-email">{t.created_by_name || t.created_by_email || '—'}</td>
                                    <td className="ticket-date">{formatDate(t.created_at)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}

export default SupportTicketsPanel;
